import { Component, Input, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs';
import { TableService } from '../../../../services/table.service';
import { Table, TableCombination, TableZone } from '../../../../models/table';
import { CellRect, layoutRect, rectsOverlap } from '../floor-plan/floor-plan-grid.util';

export const MIN_COMBINATION_TABLES = 2;
export const MAX_COMBINATION_TABLES = 4;

/** Placed tables with at most this many empty cells between them count as side by side. */
const NEXT_TO_GAP = 1;

/** Placed tables of one zone that sit next to each other, as id pairs. */
export function neighbourPairs(tables: Table[]): [string, string][] {
  const placed = tables
    .filter((t) => t.layout)
    .map((t) => ({ id: t.table_id, rect: layoutRect(t.layout!, t.shape, t.number_of_seats) }));
  // rectsOverlap is strict, so growing by one cell finds tables that touch;
  // each further cell allows one empty cell between them.
  const reach = NEXT_TO_GAP + 1;
  const grown = (r: CellRect): CellRect => ({
    x: r.x - reach,
    y: r.y - reach,
    width: r.width + 2 * reach,
    height: r.height + 2 * reach,
  });
  const pairs: [string, string][] = [];
  for (let i = 0; i < placed.length; i++) {
    for (let j = i + 1; j < placed.length; j++) {
      if (rectsOverlap(grown(placed[i].rect), placed[j].rect)) {
        pairs.push([placed[i].id, placed[j].id]);
      }
    }
  }
  return pairs;
}

/**
 * Tables → Combinations (FNF-1184): which tables the restaurant pushes
 * together for a party no single table seats. Bookings use a combination only
 * when no single table fits (reservation-service, FNF-1183). Each change is
 * saved straight away through restaurant-service (FNF-1182).
 */
@Component({
  selector: 'app-table-combinations',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './table-combinations.component.html',
  styleUrl: './table-combinations.component.css',
})
export class TableCombinationsComponent implements OnInit {
  private readonly tableService = inject(TableService);
  private readonly toastr = inject(ToastrService);
  private readonly fb = inject(FormBuilder);

  @Input() tables: Table[] = [];
  @Input() zones: TableZone[] = [];

  readonly maxTables = MAX_COMBINATION_TABLES;
  combinations: TableCombination[] = [];
  state: 'loading' | 'ready' | 'failed' = 'loading';
  saving = false;
  /** Open while adding or editing. */
  formOpen = false;
  editingId: string | null = null;
  /** The combination waiting for "Remove?" to be confirmed. */
  confirmingId: string | null = null;
  /** Party sizes follow the tables until the owner types their own. */
  private partyEdited = false;

  form = this.fb.group({
    zone: [''],
    tableIds: [[] as string[]],
    minParty: [null as number | null, [Validators.required, Validators.min(1)]],
    maxParty: [null as number | null, [Validators.required, Validators.min(1)]],
    name: ['', Validators.maxLength(40)],
  });

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.state = 'loading';
    this.tableService.listCombinations().subscribe({
      next: (list) => {
        this.combinations = list;
        this.state = 'ready';
      },
      error: () => (this.state = 'failed'),
    });
  }

  // ------------------------------------------------------------ reading

  private get byId(): Map<string, Table> {
    return new Map(this.tables.map((t) => [t.table_id, t]));
  }

  zoneTables(zoneId: string): Table[] {
    return this.tables
      .filter((t) => t.zone?.id === zoneId)
      .sort((a, b) => a.table_name.localeCompare(b.table_name, undefined, { numeric: true }));
  }

  /** Zones that have tables, so there is something to combine. */
  get zonesWithTables(): TableZone[] {
    return this.zones.filter((z) => this.zoneTables(z.id).length >= MIN_COMBINATION_TABLES);
  }

  combinationsIn(zoneId: string): TableCombination[] {
    const ids = new Set(this.zoneTables(zoneId).map((t) => t.table_id));
    return this.combinations.filter((c) => c.table_ids.every((id) => ids.has(id)));
  }

  /** "T3 + T4". */
  label(tableIds: string[]): string {
    const byId = this.byId;
    return tableIds.map((id) => byId.get(id)?.table_name ?? '?').join(' + ');
  }

  seatsOf(tableIds: string[]): number {
    const byId = this.byId;
    return tableIds.reduce((sum, id) => sum + (byId.get(id)?.number_of_seats ?? 0), 0);
  }

  /** Neighbours on the floor plan that aren't a combination yet. */
  suggestions(zoneId: string): [string, string][] {
    const taken = new Set(this.combinations.map((c) => [...c.table_ids].sort().join('|')));
    return neighbourPairs(this.zoneTables(zoneId))
      .filter((pair) => !taken.has([...pair].sort().join('|')))
      .slice(0, 6);
  }

  // ------------------------------------------------------------ editing

  get selected(): string[] {
    return this.form.value.tableIds ?? [];
  }

  isSelected(id: string): boolean {
    return this.selected.includes(id);
  }

  startAdd(zoneId: string, tableIds: string[] = []): void {
    this.editingId = null;
    this.confirmingId = null;
    this.partyEdited = false;
    this.form.reset({ zone: zoneId, tableIds, minParty: null, maxParty: null, name: '' });
    this.applyPartyDefaults();
    this.formOpen = true;
  }

  edit(c: TableCombination): void {
    const zone = this.byId.get(c.table_ids[0])?.zone?.id ?? '';
    this.editingId = c.combination_id;
    this.confirmingId = null;
    this.partyEdited = true;
    this.form.reset({
      zone,
      tableIds: [...c.table_ids],
      minParty: c.min_party,
      maxParty: c.max_party,
      name: c.name ?? '',
    });
    this.formOpen = true;
  }

  cancel(): void {
    this.formOpen = false;
    this.editingId = null;
  }

  toggleTable(id: string): void {
    const now = this.selected;
    if (!now.includes(id) && now.length >= MAX_COMBINATION_TABLES) {
      return;
    }
    this.form.patchValue({
      tableIds: now.includes(id) ? now.filter((t) => t !== id) : [...now, id],
    });
    this.applyPartyDefaults();
  }

  partyChanged(): void {
    this.partyEdited = true;
  }

  /**
   * From one more than the largest table picked, up to the seats less two:
   * pushing tables together loses the seats where they meet (two 4-tops
   * usually seat 6, not 8).
   */
  private applyPartyDefaults(): void {
    if (this.partyEdited || this.selected.length < MIN_COMBINATION_TABLES) {
      return;
    }
    const byId = this.byId;
    const largest = Math.max(...this.selected.map((id) => byId.get(id)?.number_of_seats ?? 0));
    const seats = this.seatsOf(this.selected);
    const min = Math.min(largest + 1, seats);
    this.form.patchValue({ minParty: min, maxParty: Math.max(min, seats - 2) });
  }

  /** Why the combination can't be saved yet, or null. */
  get formError(): string | null {
    const ids = this.selected;
    const { minParty, maxParty } = this.form.value;
    if (ids.length < MIN_COMBINATION_TABLES) {
      return 'Pick at least two tables.';
    }
    if (!minParty || !maxParty || minParty < 1) {
      return 'Enter the smallest and largest party.';
    }
    if (minParty > maxParty) {
      return "The smallest party can't be larger than the largest.";
    }
    const seats = this.seatsOf(ids);
    if (maxParty > seats) {
      return `These tables have ${seats} seats, fewer than ${maxParty}.`;
    }
    const wanted = [...ids].sort().join('|');
    if (
      this.combinations.some(
        (c) => c.combination_id !== this.editingId && [...c.table_ids].sort().join('|') === wanted,
      )
    ) {
      return 'A combination of these tables already exists.';
    }
    return null;
  }

  save(): void {
    if (this.formError || this.saving) {
      return;
    }
    const v = this.form.getRawValue();
    const input = {
      table_ids: v.tableIds ?? [],
      min_party: Number(v.minParty),
      max_party: Number(v.maxParty),
      name: (v.name ?? '').trim() || null,
    };
    const editing = this.editingId;
    this.saving = true;
    const request = editing
      ? this.tableService.updateCombination(editing, input)
      : this.tableService.createCombination(input);
    request.pipe(finalize(() => (this.saving = false))).subscribe({
      next: (saved) => {
        this.combinations = editing
          ? this.combinations.map((c) => (c.combination_id === editing ? saved : c))
          : [...this.combinations, saved];
        this.toastr.success(
          `${this.label(saved.table_ids)} seats ${saved.min_party}–${saved.max_party} guests.`,
          editing ? 'Combination updated' : 'Combination added',
        );
        this.cancel();
      },
      error: (err) => this.toastr.error(this.errorMessage(err), 'Error'),
    });
  }

  askRemove(c: TableCombination): void {
    this.confirmingId = c.combination_id;
  }

  remove(c: TableCombination): void {
    this.saving = true;
    this.tableService
      .deleteCombination(c.combination_id)
      .pipe(finalize(() => (this.saving = false)))
      .subscribe({
        next: () => {
          this.combinations = this.combinations.filter(
            (x) => x.combination_id !== c.combination_id,
          );
          this.confirmingId = null;
          if (this.editingId === c.combination_id) {
            this.cancel();
          }
          this.toastr.success(
            `${this.label(c.table_ids)} is no longer offered.`,
            'Combination removed',
          );
        },
        error: (err) => this.toastr.error(this.errorMessage(err), 'Error'),
      });
  }

  private errorMessage(err: any): string {
    const detail = err?.error?.error?.message ?? err?.error?.detail ?? err?.message;
    return typeof detail === 'string' ? detail : 'Unable to save the combination.';
  }
}
